/**
 * Feature Permissions Constants - Frontend
 *
 * This file defines all available feature permissions and UI navigation groups.
 * Should stay in sync with backend/constants/featurePermissions.js
 */
import { BRAND_NAME } from "../config/brand";

// All available feature permissions
export const FEATURE_PERMISSIONS = {
  // Property Management
  PROPERTIES_READ: "properties_read",
  PROPERTIES_WRITE: "properties_write",
  PROPERTIES_UPDATE: "properties_update",
  PROPERTIES_DELETE: "properties_delete",
  PROPERTIES_TRANSFER: "properties_transfer",
  PROPERTIES_TAKEOVER: "properties_takeover",
  PROPERTIES_PUBLIC_LISTING: "properties_public_listing",
  PROPERTIES_SHAREDCHARGES_READ: "properties_sharedcharges_read",
  PROPERTIES_SHAREDCHARGES_WRITE: "properties_sharedcharges_write",
  RENTALUNIT_READ: "rentalunit_read",
  RENTALUNIT_WRITE: "rentalunit_write",
  RENTALUNIT_UPDATE: "rentalunit_update",
  RENTALUNIT_DELETE: "rentalunit_delete",
  BULK_METER_RECORDING: "bulk_meter_recording",

  // Tenant Management
  TENANTS_READ: "tenants_read",
  TENANTS_WRITE: "tenants_write",
  TENANTS_UPDATE: "tenants_update",
  TENANTS_DELETE: "tenants_delete",
  TENANTS_ALLOW_MULTIPLE: "tenants_allow_multiple",
  BOOKINGS_READ: "bookings_read",
  BOOKINGS_WRITE: "bookings_write",
  BOOKINGS_UPDATE: "bookings_update",
  BOOKINGS_DELETE: "bookings_delete",

  // Financial Management
  PAYMENTS_READ: "payments_read",
  PAYMENTS_WRITE: "payments_write",
  PAYMENTS_UPDATE: "payments_update",
  PAYMENTS_DELETE: "payments_delete",
  PG_ACCOUNTS_READ: "pg_accounts_read",
  PG_ACCOUNTS_WRITE: "pg_accounts_write",
  PG_ACCOUNTS_UPDATE: "pg_accounts_update",
  PG_ACCOUNTS_DELETE: "pg_accounts_delete",
  PG_ACCOUNTS_RELOAD: "pg_accounts_reload",
  INVOICES_READ: "invoices_read",
  INVOICES_WRITE: "invoices_write",
  INVOICES_UPDATE: "invoices_update",
  INVOICES_DELETE: "invoices_delete",
  INVOICES_CHANGE_VENDOR: "invoices_change_vendor",
  INVOICES_BULK_GENERATION: "invoices_bulk_generation",
  INVOICES_PROPERTYWISE: "invoices_propertywise",
  AUTO_BILLING: "auto_billing",
  INVOICE_DUE_REMINDER: "invoice_due_reminder",
  INVOICE_LATE_FEE: "invoice_late_fee",
  LATE_FEE_STEPUP: "late_fee_stepup",
  LATE_FEE_RETRO_STEPUP: "late_fee_retro_stepup",

  // Maintenance Management
  MAINTENANCE_READ: "maintenance_read",
  MAINTENANCE_WRITE: "maintenance_write",
  MAINTENANCE_UPDATE: "maintenance_update",
  MAINTENANCE_DELETE: "maintenance_delete",
  MAINTENANCE_ASSIGN: "maintenance_assign",
  MAINTENANCE_CLOSECODE: "maintenance_closecode",
  INVENTORY_CATEGORY_WRITE: "inventory_category_write",
  INVENTORY_CATEGORY_UPDATE: "inventory_category_update",
  INVENTORY_CATEGORY_DELETE: "inventory_category_delete",
  INVENTORY_ITEMS_CREATE: "inventory_items_create",
  INVENTORY_ITEMS_UPDATE: "inventory_items_update",
  INVENTORY_ITEMS_READ: "inventory_items_read",
  INVENTORY_ITEMS_DELETE: "inventory_items_delete",

  // Expense Management
  EXPENSES_READ: "expenses_read",
  EXPENSES_WRITE: "expenses_write",
  EXPENSES_UPDATE: "expenses_update",
  EXPENSES_DELETE: "expenses_delete",

  // Company Management
  COMPANY_READ: "company_read",
  COMPANY_WRITE: "company_write",
  COMPANY_UPDATE: "company_update",
  COMPANY_DELETE: "company_delete",
  INTEGRATIONS_TAB: "integrations_tab",
  USERS_READ: "users_read",
  USERS_WRITE: "users_write",
  USERS_UPDATE: "users_update",
  USERS_DELETE: "users_delete",

  // Analytics & Reporting
  ANALYTICS_READ: "analytics_read",
  REPORTS_READ: "reports_read",
  ANALYTICS_EXPORT: "analytics_export",
  PROFIT_LOSS_EXPORT: "profit_loss_export",

  // System Administration
  ADMIN_READ: "admin_read",
  ADMIN_WRITE: "admin_write",
  ADMIN_UPDATE: "admin_update",
  ADMIN_DELETE: "admin_delete",
  NOTIFICATIONS_READ: "notifications_read",
  NOTIFICATIONS_WRITE: "notifications_write",
  NOTIFICATIONS_UPDATE: "notifications_update",
  NOTIFICATIONS_DELETE: "notifications_delete",
  BROADCAST_READ: "broadcast_read",
  BROADCAST_WRITE: "broadcast_write",
  BROADCAST_UPDATE: "broadcast_update",
  BROADCAST_DELETE: "broadcast_delete",
  COMPANY_CATALOGUE: "company_catalogue",
  WHATSAPP_NOTIFICATIONS: "whatsapp_notifications",
  EMAIL_NOTIFICATIONS: "email_notifications",

  // Catalogue Premium Features
  CUSTOM_CSS: "custom_css",
  CATALOGUE_BRANDING_REMOVAL: "catalogue_branding_removal",
  CUSTOM_DOMAIN: "custom_domain",
} as const;

// Type for feature permissions
export type FeaturePermission =
  (typeof FEATURE_PERMISSIONS)[keyof typeof FEATURE_PERMISSIONS];

// Array of all valid feature permissions
export const ALL_FEATURE_PERMISSIONS: FeaturePermission[] =
  Object.values(FEATURE_PERMISSIONS);

// Type for permission detail
export interface PermissionDetail {
  id: string;
  name: string;
  description: string;
}

// Type for permission category
export interface PermissionCategory {
  category: string;
  permissions: PermissionDetail[];
}

// Feature permissions with detailed information for UI display
export const FEATURE_PERMISSIONS_DETAILS: PermissionCategory[] = [
  {
    category: "Property Management",
    permissions: [
      {
        id: FEATURE_PERMISSIONS.PROPERTIES_READ,
        name: "View Properties",
        description: "Can view property listings, details, and related information"
      },
      {
        id: FEATURE_PERMISSIONS.PROPERTIES_WRITE,
        name: "Create Properties",
        description: "Can create new properties"
      },
      {
        id: FEATURE_PERMISSIONS.PROPERTIES_UPDATE,
        name: "Update Properties",
        description: "Can edit and modify existing properties"
      },
      {
        id: FEATURE_PERMISSIONS.PROPERTIES_DELETE,
        name: "Delete Properties",
        description: "Can remove properties from the system"
      },
      {
        id: FEATURE_PERMISSIONS.PROPERTIES_SHAREDCHARGES_WRITE,
        name: "Manage Shared Charges",
        description: "Can create and update shared charges"
      },
      {
        id: FEATURE_PERMISSIONS.RENTALUNIT_READ,
        name: "View Rental Units",
        description: "Can view rental unit details and availability"
      },
      {
        id: FEATURE_PERMISSIONS.RENTALUNIT_WRITE,
        name: "Create Rental Units",
        description: "Can create new rental units"
      },
      {
        id: FEATURE_PERMISSIONS.RENTALUNIT_UPDATE,
        name: "Update Rental Units",
        description: "Can edit and modify existing rental units"
      },
      {
        id: FEATURE_PERMISSIONS.RENTALUNIT_DELETE,
        name: "Delete Rental Units",
        description: "Can remove rental units from the system"
      },
      {
        id: FEATURE_PERMISSIONS.BULK_METER_RECORDING,
        name: "Bulk Meter Recording",
        description: "Can record meter readings in bulk for all units"
      }
    ]
  },
  {
    category: "Tenant Management",
    permissions: [
      {
        id: FEATURE_PERMISSIONS.TENANTS_READ,
        name: "View Tenants",
        description: "Can view tenant information and agreements"
      },
      {
        id: FEATURE_PERMISSIONS.TENANTS_WRITE,
        name: "Create Tenants",
        description: "Can add new tenant records"
      },
      {
        id: FEATURE_PERMISSIONS.TENANTS_UPDATE,
        name: "Update Tenants",
        description: "Can edit and modify tenant information"
      },
      {
        id: FEATURE_PERMISSIONS.TENANTS_DELETE,
        name: "Delete Tenants",
        description: "Can remove tenant records from the system"
      },
      {
        id: FEATURE_PERMISSIONS.TENANTS_ALLOW_MULTIPLE,
        name: "Allow Multiple Tenants",
        description: "Can allow multiple tenants for a single rental unit"
      },
      {
        id: FEATURE_PERMISSIONS.BOOKINGS_READ,
        name: "View Bookings",
        description: "Can view booking requests and schedules"
      },
      {
        id: FEATURE_PERMISSIONS.BOOKINGS_WRITE,
        name: "Create Bookings",
        description: "Can create new bookings"
      },
      {
        id: FEATURE_PERMISSIONS.BOOKINGS_UPDATE,
        name: "Update Bookings",
        description: "Can modify existing bookings"
      },
      {
        id: FEATURE_PERMISSIONS.BOOKINGS_DELETE,
        name: "Cancel Bookings",
        description: "Can cancel and delete bookings"
      }
    ]
  },
  {
    category: "Financial Management",
    permissions: [
      {
        id: FEATURE_PERMISSIONS.PAYMENTS_READ,
        name: "View Payments",
        description: "Can view payment records and transaction history"
      },
      {
        id: FEATURE_PERMISSIONS.PAYMENTS_WRITE,
        name: "Record Payments",
        description: "Can record new payments and transactions"
      },
      {
        id: FEATURE_PERMISSIONS.PAYMENTS_UPDATE,
        name: "Update Payments",
        description: "Can modify payment records and issue adjustments"
      },
      {
        id: FEATURE_PERMISSIONS.PAYMENTS_DELETE,
        name: "Delete Payments",
        description: "Can void or delete payment records"
      },
        // add the description for follwoing permissions
        
      //  'pg_accounts_write': 'Manage Payment Gateway Accounts',
      // 'pg_accounts_read': 'View Payment Gateway Accounts',
      // 'pg_accounts_update': 'Update Payment Gateway Accounts',
      // 'pg_accounts_delete': 'Delete Payment Gateway Accounts',
      // 'pg_accounts_reload': 'Reload Payment Gateway Accounts'

      {
        id: FEATURE_PERMISSIONS.PG_ACCOUNTS_READ,
        name: "View Payment Gateway Accounts",
        description: "Can view payment gateway account details"
      },
      {
        id: FEATURE_PERMISSIONS.PG_ACCOUNTS_WRITE,
        name: "Manage Payment Gateway Accounts",
        description: "Can add the payment gateway accounts"
      },
      {
        id: FEATURE_PERMISSIONS.PG_ACCOUNTS_DELETE,
        name: "Delete Payment Gateway Accounts",
        description: "Can remove payment gateway accounts"
      },
      {
        id: FEATURE_PERMISSIONS.PG_ACCOUNTS_UPDATE,
        name: "Update Payment Gateway Accounts",
        description: "Can edit payment gateway configuration and account details"
      },
      {
        id: FEATURE_PERMISSIONS.PG_ACCOUNTS_RELOAD,
        name: "Reload Payment Gateway Accounts",
        description: "Can re-sync payment gateway account status"
      },

      {
        id: FEATURE_PERMISSIONS.INVOICES_READ,
        name: "View Invoices",
        description: "Can view invoices and billing statements"
      },
      {
        id: FEATURE_PERMISSIONS.INVOICES_WRITE,
        name: "Create Invoices",
        description: "Can generate new invoices"
      },
      {
        id: FEATURE_PERMISSIONS.INVOICES_UPDATE,
        name: "Update Invoices",
        description: "Can edit and modify existing invoices"
      },
      {
        id: FEATURE_PERMISSIONS.INVOICES_DELETE,
        name: "Delete Invoices",
        description: "Can cancel or delete invoices"
      },
      {
        id: FEATURE_PERMISSIONS.INVOICES_CHANGE_VENDOR,
        name: "Change Payment Gateway Vendor",
        description: "Can change the payment gateway vendor on existing invoices"
      },
      {
        id: FEATURE_PERMISSIONS.INVOICES_PROPERTYWISE,
        name: "Property Wise Invoices",
        description: "Can view property wise invoice statistics and breakdown"
      },
      {
        id: FEATURE_PERMISSIONS.AUTO_BILLING,
        name: "Auto Billing",
        description: "Enables automatic invoice generation on scheduled dates"
      },
      {
        id: FEATURE_PERMISSIONS.INVOICE_DUE_REMINDER,
        name: "Invoice Due Reminders",
        description: "Enables payment reminder notifications before invoice due dates"
      },
      {
        id: FEATURE_PERMISSIONS.INVOICE_LATE_FEE,
        name: "Invoice Late Fee",
        description: "Enables automatic late fee charges on overdue invoices"
      },
      {
        id: FEATURE_PERMISSIONS.LATE_FEE_STEPUP,
        name: "Late Fee Step Up",
        description: "Enables tiered late fee rates based on days overdue"
      },
      {
        id: FEATURE_PERMISSIONS.LATE_FEE_RETRO_STEPUP,
        name: "Late Fee Retro Step Up",
        description: "Enables retroactive tiered late fee recalculation"
      }
    ]
  },
  {
    category: "Maintenance",
    permissions: [
      {
        id: FEATURE_PERMISSIONS.MAINTENANCE_READ,
        name: "View Maintenance",
        description: "Can view maintenance requests and work orders"
      },
      {
        id: FEATURE_PERMISSIONS.MAINTENANCE_WRITE,
        name: "Create Maintenance",
        description: "Can create new maintenance requests"
      },
      {
        id: FEATURE_PERMISSIONS.MAINTENANCE_UPDATE,
        name: "Update Maintenance",
        description: "Can modify and update maintenance requests"
      },
      {
        id: FEATURE_PERMISSIONS.MAINTENANCE_DELETE,
        name: "Delete Maintenance",
        description: "Can cancel or delete maintenance requests"
      },
      {
        id: FEATURE_PERMISSIONS.MAINTENANCE_ASSIGN,
        name: "Assign Maintenance",
        description: "Can assign maintenance tasks to staff members"
      },
      {
        id: FEATURE_PERMISSIONS.MAINTENANCE_CLOSECODE,
        name: "Maintenance Close Code",
        description: "Can toggle maintenance close code requirement"
      },
      {
        id: FEATURE_PERMISSIONS.INVENTORY_CATEGORY_WRITE,
        name: "Create Inventory Categories",
        description: "Can add new inventory categories"
      },
      {
        id: FEATURE_PERMISSIONS.INVENTORY_CATEGORY_UPDATE,
        name: "Update Inventory Categories",
        description: "Can edit inventory categories"
      },
      {
        id: FEATURE_PERMISSIONS.INVENTORY_CATEGORY_DELETE,
        name: "Delete Inventory Categories",
        description: "Can remove inventory categories when empty"
      },
      {
        id: FEATURE_PERMISSIONS.INVENTORY_ITEMS_CREATE,
        name: "Create Inventory Items",
        description: "Can add new inventory items"
      },
      {
        id: FEATURE_PERMISSIONS.INVENTORY_ITEMS_UPDATE,
        name: "Update Inventory Items",
        description: "Can adjust stock and edit inventory items"
      },
      {
        id: FEATURE_PERMISSIONS.INVENTORY_ITEMS_READ,
        name: "View Inventory Items",
        description: "Can view inventory items, categories, and history"
      },
      {
        id: FEATURE_PERMISSIONS.INVENTORY_ITEMS_DELETE,
        name: "Delete Inventory Items",
        description: "Can remove inventory items"
      }
    ]
  },
  {
    category: "Company Management",
    permissions: [
      {
        id: FEATURE_PERMISSIONS.COMPANY_READ,
        name: "View Company Info",
        description: "Can view company details and settings"
      },
      {
        id: FEATURE_PERMISSIONS.COMPANY_WRITE,
        name: "Create Company Settings",
        description: "Can add new company configurations"
      },
      {
        id: FEATURE_PERMISSIONS.COMPANY_UPDATE,
        name: "Update Company",
        description: "Can edit company information and settings"
      },
      {
        id: FEATURE_PERMISSIONS.COMPANY_DELETE,
        name: "Delete Company Settings",
        description: "Can remove company configurations"
      },
      {
        id: FEATURE_PERMISSIONS.INTEGRATIONS_TAB,
        name: "Manage Integrations",
        description: "Can access company integrations setup and connected channels"
      },
      {
        id: FEATURE_PERMISSIONS.USERS_READ,
        name: "View Users",
        description: "Can view company users and their roles"
      },
      {
        id: FEATURE_PERMISSIONS.USERS_WRITE,
        name: "Add Users",
        description: "Can add new company users"
      },
      {
        id: FEATURE_PERMISSIONS.USERS_UPDATE,
        name: "Update Users",
        description: "Can edit user roles and permissions"
      },
      {
        id: FEATURE_PERMISSIONS.USERS_DELETE,
        name: "Remove Users",
        description: "Can deactivate or remove company users"
      }
    ]
  },
  {
    category: "Analytics & Reports",
    permissions: [
      {
        id: FEATURE_PERMISSIONS.ANALYTICS_READ,
        name: "View Analytics",
        description: "Can access analytics dashboards and metrics"
      },
      {
        id: FEATURE_PERMISSIONS.REPORTS_READ,
        name: "View Reports",
        description: "Can access and view generated reports"
      },
      {
        id: FEATURE_PERMISSIONS.ANALYTICS_EXPORT,
        name: "Export Analytics",
        description: "Can export analytics data and reports"
      },
      {
        id: FEATURE_PERMISSIONS.PROFIT_LOSS_EXPORT,
        name: "Profit & Loss Export",
        description: "Can view and export P/L reports"
      }
    ]
  },
  // {
  //   category: "System Administration",
  //   permissions: [
  //     {
  //       id: FEATURE_PERMISSIONS.ADMIN_READ,
  //       name: "View Admin Settings",
  //       description: "Can view system administration settings and configurations"
  //     },
  //     {
  //       id: FEATURE_PERMISSIONS.ADMIN_WRITE,
  //       name: "Create Admin Settings",
  //       description: "Can add new system configurations and settings"
  //     },
  //     {
  //       id: FEATURE_PERMISSIONS.ADMIN_UPDATE,
  //       name: "Update Admin Settings",
  //       description: "Can modify system settings and configurations"
  //     },
  //     {
  //       id: FEATURE_PERMISSIONS.ADMIN_DELETE,
  //       name: "Delete Admin Settings",
  //       description: "Can remove system configurations"
  //     }
  //   ]
  // },
  {
    category: "Notifications & Communications",
    permissions: [
      {
        id: FEATURE_PERMISSIONS.BROADCAST_READ,
        name: "View BROADCAST",
        description: "Can view personal BROADCAST and alerts"
      },
      {
        id: FEATURE_PERMISSIONS.BROADCAST_WRITE,
        name: "Send BROADCAST",
        description: "Can create and send BROADCAST"
      },
      {
        id: FEATURE_PERMISSIONS.BROADCAST_UPDATE,
        name: "Update BROADCAST",
        description: "Can mark BROADCAST as read and manage preferences"
      },
      {
        id: FEATURE_PERMISSIONS.BROADCAST_DELETE,
        name: "Delete BROADCAST",
        description: "Can archive and delete BROADCAST"
      },
      {
        id: FEATURE_PERMISSIONS.WHATSAPP_NOTIFICATIONS,
        name: "WhatsApp Notifications",
        description: "Can send WhatsApp notifications to tenants"
      },
      {
        id: FEATURE_PERMISSIONS.EMAIL_NOTIFICATIONS,
        name: "Email Notifications",
        description: "Can send email notifications to tenants"
      }
    ]
  },
  {
    category: "Catalogue",
    permissions: [
      {
        id: FEATURE_PERMISSIONS.CUSTOM_CSS,
        name: "Custom CSS",
        description: "Can add custom CSS overrides to the public catalogue"
      },
      {
        id: FEATURE_PERMISSIONS.CATALOGUE_BRANDING_REMOVAL,
        name: "Remove Branding",
        description: `Can remove ${BRAND_NAME} branding from the public catalogue`
      },
      {
        id: FEATURE_PERMISSIONS.CUSTOM_DOMAIN,
        name: "Custom Domain",
        description: "Can connect a custom domain to the public catalogue"
      }
    ]
  }
];

// Helper function to get permission details by ID
export const getPermissionDetails = (permissionId: string): (PermissionDetail & { category: string }) | null => {
  for (const category of FEATURE_PERMISSIONS_DETAILS) {
    const permission = category.permissions.find((p: PermissionDetail) => p.id === permissionId);
    if (permission) {
      return {
        ...permission,
        category: category.category
      };
    }
  }
  return null;
};
